// Game data (sim/data/gamedata.json, generated by tools/data/export_sim_data.py)
// compiled into level-scaled runtime definitions. Every number here comes from
// the build's own files; see docs/DATA_FIELDS.md for field meanings and units.

export type Rec = Record<string, unknown>;

export interface GameData {
  build: string;
  globals: Record<string, unknown>;
  timelines: Record<string, Timeline>;
  towerScaling: {
    king_tower: { Hitpoints: number[]; Damage: number[] };
    princess_tower: { Hitpoints: number[]; Damage: number[] };
  };
  powerMultiplier: number[];
  rarities: Record<string, { RelativeLevel: number; LevelCount: number; TournamentLevelIndex: number }>;
  tilemap: { width: number; height: number; grid: number[] };
  spawnGroups: Record<string, { Objects: { Data: string; x: number; y: number }[] }>;
  units: Record<string, Rec>;
  projectiles: Record<string, Rec>;
  areaEffects: Record<string, Rec>;
  buffs: Record<string, Rec>;
  cards: Record<string, Rec>;
  filters: Record<string, Rec>;
  damageTypes: Record<string, Rec>;
}

export interface Timeline {
  startingElixir: number;
  sections: { lengthSec: number; type: string }[];
  elixirRates: { fromSec: number; toSec: number; fullBarMs: number }[];
  raw: Rec;
}

export const TICK_MS = 50;
export const ELIXIR_ONE = 10000;

function num(r: Rec, k: string, d = 0): number {
  const v = r[k];
  return typeof v === 'number' ? v : d;
}
function bool(r: Rec, k: string): boolean {
  return r[k] === true;
}
function str(r: Rec, k: string): string | null {
  const v = r[k];
  return typeof v === 'string' ? v : null;
}

/**
 * Mass as normalised by the native character loader [STAT 0xdde618 @0xdded9c]: a missing Mass is
 * ((r²/250, unsigned)·r)/62500 from the collision radius (r 500 -> 8, 600 -> 13, 1000 -> 64),
 * then clamped to [1, 20].
 */
export function normaliseMass(mass: number, radius: number): number {
  let m = mass;
  if (m === 0) m = Math.trunc((Math.trunc(((radius * radius) >>> 0) / 250) * radius) / 62500);
  if (m > 20) m = 20;
  if (m <= 1) m = 1;
  return m;
}

/**
 * Walking speed (+0x410, read by vtb8 0xf82a18) as the character loader 0xdde618 leaves it [STAT
 * @0xddf844]: with StopMovementAfterMS s > 0 (+0x328) and WaitMS w (+0x32c) it is
 * Speed · ((s + w) · 1000 / s) / 1000, both divisions truncated, so the stop-and-go average stays near
 * the data Speed (Giant 45 -> 52, Golem 45 -> 54); +0x414 keeps the data value.
 */
export function walkSpeed(r: Rec): number {
  const v = num(r, 'Speed');
  const s = num(r, 'StopMovementAfterMS');
  if (s <= 0) return v;
  const k = Math.trunc(((s + num(r, 'WaitMS')) * 1000) / s);
  return Math.trunc((k * v) / 1000);
}

/**
 * Card stat scaling, Tables_scaleByLevel 0xe7e418 for kinds 1 (damage) / 2 (hitpoints) with a rarity
 * [STAT]: the actor level of a card at level L is n = L − 1 − RelativeLevel; n < 1 gives the base; else
 * m = the rarity's PowerLevelMultiplier[n − 1] (0xe612f4) while n − 1 is inside its table, beyond it 100
 * grown n times by v + v/10 (truncated), and the stat is (m · base) / 100 truncated. The tables of all
 * rarities are prefixes of Common's (rarities.csv), the only one exported (`powerMultiplier` = [100, ...]);
 * a rarity's own table is shorter (Rare 17, Epic 14, Legendary 11, Champion / Experimental 9 entries) and
 * the client iterates past it, which differs from Common's entries (450 vs 449 at n = 16) only from card
 * level 21 (Rare) up; no card goes above level 16.
 */
export function scaleStat(data: GameData, base: number, level: number, rarity: string | null): number {
  const rel = rarity && data.rarities[rarity] ? data.rarities[rarity].RelativeLevel : 0;
  const n = level - 1 - rel;
  if (n < 1) return base;
  const table = data.powerMultiplier; // table[n] = PowerLevelMultiplier[n - 1]
  let m: number;
  if (n < table.length) m = table[n];
  else {
    m = 100;
    for (let i = 0; i < n; i++) m = Math.trunc(m / 10) + m;
  }
  return Math.trunc((m * base) / 100);
}

/**
 * Crown-tower scaling, Tables_scaleByLevel 0xe7e418 for kinds 3 (King damage), 4 (King hitpoints),
 * 5 (Princess tower damage), 6 (Princess tower hitpoints) [STAT]: the tower's actor level n = L − 1
 * iterations from m = 100, iteration i growing by the kind's *_INCREASE_PERCENT_PER_{KING,TOWER}_LEVEL,
 * its _AT_TOURNAMENTCAP value at i = TOWER_SCALING_START_EXP_LEVEL − 1 and its _AFTER_TOURNAMENTCAP value
 * from TOWER_SCALING_START_EXP_LEVEL on (the tower data's Common rarity shifts the start by 0):
 * m = ((p + 100) · m) / 100, or (p + 100) · (m / 100) from 100000 on; the stat is (m · base) / 100.
 * Projectiles with a DamageScalingMode (KingTower 1, PrincessTower 2, loader 0xe5b14c) scale their
 * damage with kind 2·mode + 1 (0xe5ae54).
 */
export function scaleTower(data: GameData, base: number, level: number, kind: 3 | 4 | 5 | 6): number {
  const G = data.globals;
  const g = (k: string): number => (typeof G[k] === 'number' ? (G[k] as number) : 0);
  const what = kind === 3 || kind === 5 ? 'DAMAGE' : 'HITPOINT';
  const who = kind === 3 || kind === 4 ? 'KING' : 'TOWER';
  const pBase = g(`${what}_INCREASE_PERCENT_PER_${who}_LEVEL`);
  const pAfter = g(`${what}_INCREASE_PERCENT_PER_${who}_LEVEL_AFTER_TOURNAMENTCAP`);
  const pAt = g(`${what}_INCREASE_PERCENT_PER_${who}_LEVEL_AT_TOURNAMENTCAP`);
  const start = g('TOWER_SCALING_START_EXP_LEVEL');
  let m = 100;
  for (let i = 0; i < level - 1; i++) {
    const p = i === start - 1 ? pAt : i >= start ? pAfter : pBase;
    m = m > 99999 ? (p + 100) * Math.trunc(m / 100) : Math.trunc(((p + 100) * m) / 100);
  }
  return Math.trunc((m * base) / 100);
}

/**
 * The attack sequence the character loader 0xdde618 builds when the data has no AttackSequenceList [STAT]:
 * entry 1 (VariableDamageTime1, Damage) always; with VariableDamageTime1 >= 1 entry 2 (VariableDamageTime2,
 * VariableDamage2) and mode 3; with VariableDamageTime2 >= 1 also entry 3 (no time, VariableDamage3). The
 * loader reads absent times as 0. Characters with an AttackSequence / AttackSequenceList column are not
 * supported (those columns are not in UNIT_FIELDS_OK).
 */
function attackSequence(r: Rec, scale: (v: number) => number, baseDamage: number): { seqMode: number; seq: AttackSeqEntry[] } {
  const entry = (damage: number, duration: number): AttackSeqEntry => ({ damage: scale(damage), duration, clock: 100, range: -1, sight: -1, minRange: -1 });
  const t1 = num(r, 'VariableDamageTime1');
  const seq = [entry(baseDamage, t1)];
  if (t1 < 1) return { seqMode: 0, seq };
  const t2 = num(r, 'VariableDamageTime2');
  seq.push(entry(num(r, 'VariableDamage2'), t2));
  if (t2 >= 1) seq.push(entry(num(r, 'VariableDamage3'), 0));
  return { seqMode: 3, seq };
}

/** Damage may be a number, {BaseDamage, TowerDamage}, or the name of a DAMAGE_TYPE. */
function damageOf(r: Rec, types?: Record<string, Rec>): { base: number; tower: number } {
  let v = r.Damage;
  if (typeof v === 'string' && types && types[v]) v = types[v];
  if (typeof v === 'number') return { base: v, tower: -1 };
  if (v && typeof v === 'object') {
    const o = v as Rec;
    return { base: num(o, 'BaseDamage'), tower: typeof o.TowerDamage === 'number' ? (o.TowerDamage as number) : -1 };
  }
  return { base: 0, tower: -1 };
}

export interface ProjectileDef {
  name: string;
  speed: number; // game units per tick
  damage: number; // scaled
  towerDamage: number; // absolute crown-tower damage, -1 if none
  crownTowerDamagePercent: number; // e.g. -77 => 23 %
  radius: number; // splash radius, 0 = single target
  homing: boolean;
  aoeToAir: boolean;
  aoeToGround: boolean;
  onlyEnemies: boolean;
  pushback: number;
  pushbackAll: boolean;
  /** Buff applied to everything the projectile damages (Ice Spirit freeze, Snowball slow...). */
  targetBuff: BuffDef | null;
  buffTime: number; // ms
  /** Chain lightning: total targets and hop radius (Electro Dragon, Electro Spirit). */
  chainCount: number;
  chainRadius: number;
  /** ProjectileRange (+0x234): flight length from the shooter, set by setAim 0xf8fb34 (0 = fly to the aim point). */
  range: number;
  /**
   * Piercing flight 0xe5a908 [STAT]: ProjectileRadius > 0 and (not Homing with ProjectileRange >= 1,
   * or CheckCollisions, or Scatter "Circle"). Such a projectile hits everything its
   * ProjectileRadius / ProjectileRadiusY box touches along the way (0xf8f2d0) instead of a target.
   */
  pierce: boolean;
  /** ProjectileRadius (+0x168) and ProjectileRadiusY (+0x16c): the collision circle or box of the flight. */
  pierceRadius: number;
  pierceRadiusY: number;
  /** MinDistance (+0x198): the aim is pushed out to at least this far from the start (0xf8e6fc). */
  minDistance: number;
  /** PingpongVisualTime (+0x1c0): out-and-back flight of this many ms (Executioner's axe). */
  pingpongTime: number;
  /** Gravity (+0x188) and ConstantHeight (+0x1bc): flight height (0xf927ec), for the view. */
  gravity: number;
  constantHeight: number;
  /** HomingTime (+0x208) / HomingMinDistance (+0x20c): re-aim at the target for this long (Magic Archer). */
  homingTime: number;
  homingMinDistance: number;
  /** CheckCollisions (+0x1a4): a piercing projectile that stops at its first living hit (Hunter). */
  checkCollisions: boolean;
  /** ApplyBuffBeforeDamage (+0x231). */
  applyBuffBeforeDamage: boolean;
  /** Characters spawned where the projectile lands (Goblin Barrel, Barbarian Barrel): SpawnCharacter (+0xf8),
   * SpawnCharacterCount (max(v,1) with a character, +0x104), SpawnCharacterDeployTime (+0x100), SpawnConstPriority (+0x1a3). */
  spawnUnit: UnitDef | null;
  spawnCount: number;
  spawnDeployTime: number;
  spawnConstPriority: boolean;
  /** Follow-up projectile spawned on impact (Log -> rolling log) (+0x138), SpawnChain (max(v,1), +0x220). */
  spawnProjectile: ProjectileDef | null;
  spawnChain: number;
  /** This projectile's SpawnCount (+0x228, copies spawned by its parent) and SpawnRadius (+0x22c, their angle spread). */
  spawnProjCount: number;
  spawnProjRadius: number;
  /** SpawnAxisX / SpawnAxisY (+0x224/+0x225), ChainIsNewProjectile (+0x1b8). */
  spawnAxisX: boolean;
  spawnAxisY: boolean;
  chainIsNew: boolean;
  /** MaximumTargets of the splash (+0x174; < 1 = all) and DoDirectionalPushback (+0x2d8). */
  maxTargets: number;
  directionalPushback: boolean;
  /**
   * ProjectileStartExtraRadius (+0x210): a piercing projectile without PingpongVisualTime runs its
   * collision step once where it is added to the world (projectile vt+0x28 0xf934bc), with
   * ProjectileRadius + this radius (Magic Archer's arrow 400).
   */
  startExtraRadius: number;
}

/**
 * One attack-sequence entry (LogicAttackSequenceData, ctor 0xdc9f34). The character loader 0xdde618 builds
 * the entries of a character without an AttackSequenceList from its VariableDamage columns: entry 1 lasts
 * VariableDamageTime1 with Damage, entry 2 VariableDamageTime2 with VariableDamage2, entry 3 has no
 * duration and VariableDamage3; mode 3 (by timer) once VariableDamageTime1 >= 1, a 2-entry sequence when
 * VariableDamageTime2 < 1.
 */
export interface AttackSeqEntry {
  /** Damage (+0x90), scaled like the character's Damage (0xde0cec: kind 1). */
  damage: number;
  /** Time (+0xa0, ms) the timer spends in this entry (0xde0e5c). */
  duration: number;
  /** Clock percent (+0xe8, ctor 100): the attack clock steps by clock / 2 per tick (0xfc90b0). */
  clock: number;
  /** Range (+0xf8), sight range (+0xfc) and minimum range (+0x100) overrides; -1 = the character's. */
  range: number;
  sight: number;
  minRange: number;
}

/** Object filter (game_object_filters.toml): which actors an area effect touches. */
export interface FilterDef {
  name: string;
  enemy: boolean;
  own: boolean;
  /** MatchTypeBuildings: buildings only; MatchTowers: crown towers only. */
  buildingsOnly: boolean;
  towersOnly: boolean;
  exBuildings: boolean;
  exKing: boolean;
  exPrincess: boolean;
  exTowers: boolean;
  exFlying: boolean;
  include: string[] | null;
  exclude: string[] | null;
}

export interface BuffDef {
  name: string;
  /** Percent: >= 100 is a haste factor (130 = x1.3), < 0 a slow (-30 = x0.7, -100 = stop). 0 = none. */
  speedMul: number;
  hitSpeedMul: number;
  spawnSpeedMul: number;
  dps: number; // scaled damage per second
  hitFrequency: number; // ms between damage/heal ticks (< 0: one tick; 0: none)
  /** CrownTowerDamagePerHit (+0xf4, scaled): a crown tower's damage per tick instead of the percentage. */
  crownTowerDamagePerHit: number;
  hps: number; // scaled heal per second
  crownTowerDamagePercent: number;
  buildingDamagePercent: number; // 0 = none (350 = x3.5 vs buildings)
  damageReduction: number;
  stacking: boolean;
  unsupported: string[];
}

export interface AreaEffectDef {
  name: string;
  radius: number;
  lifeDuration: number; // ms
  hitSpeed: number; // ms between hits, 0 = single hit
  hitSpeedOffset: number; // ms before the first hit
  damage: number;
  towerDamage: number;
  crownTowerDamagePercent: number;
  buff: BuffDef | null;
  buffTime: number;
  capBuffTime: boolean;
  filter: FilterDef | null;
  onlyEnemies: boolean;
  hitBiggest: boolean;
  maxTargets: number;
  oneHitPerTarget: boolean;
  projectile: ProjectileDef | null;
  spawnAreaEffect: AreaEffectDef | null;
  pushback: number;
  pushbackAll: boolean;
  relativePushback: boolean;
  /** One projectile per target (TargetProjectiles); else one centre projectile per pulse. */
  targetProjectiles: boolean;
  projectileStartHeight: number;
  unsupported: string[];
}

export interface UnitDef {
  name: string;
  kind: 'character' | 'building';
  rarity: string | null;
  level: number;
  hitpoints: number;
  shieldHitpoints: number;
  damage: number;
  crownTowerDamagePercent: number;
  hitSpeed: number; // ms
  loadTime: number; // ms
  range: number;
  minimumRange: number;
  sightRange: number;
  /** SightClip / SightClipSide: how far behind / sideways a unit looks for targets. */
  sightClip: number;
  sightClipSide: number;
  /** Walking speed in game units per tick, as the loader leaves it (walkSpeed: stop-and-go units faster). */
  speed: number;
  collisionRadius: number;
  mass: number;
  deployTime: number; // ms
  deployDelay: number; // ms
  attacksGround: boolean;
  attacksAir: boolean;
  targetOnlyBuildings: boolean;
  targetOnlyTroops: boolean;
  flyingHeight: number; // > 0 => air unit
  projectile: ProjectileDef | null;
  projectileStartRadius: number;
  projectileYOffset: number;
  /** ProjectileStartZ (+0x324): launch height above the shooter (0xf85d80). */
  projectileStartZ: number;
  /** OnStartingAction JumpHack_Check_Jump_Interval: no knockback while jumping (JumpHack_Ignore_Pushback_Buff). */
  jumpHack: boolean;
  /** JumpHeight (+0x44c): river-jump arc height recorded by 0xfccd94. */
  jumpHeight: number;
  areaDamageRadius: number;
  selfAsAoeCenter: boolean;
  ignorePushback: boolean;
  kamikaze: boolean;
  lifeTime: number;
  deathAreaEffect: AreaEffectDef | null;
  deathSpawn: { unit: UnitDef; count: number; radius: number; deployTime: number } | null;
  /**
   * Periodic spawner (Witch, Tombstone, huts), CharacterData loader 0xde29a4.. (absent ints are 0): SpawnNumber
   * (+0x478), SpawnPauseTime (+0x47c), SpawnInterval (+0x474), SpawnStartTime (+0x470, the first timer),
   * SpawnRadius (+0x2c0), SpawnAngleShift (+0x38c), SpawnCharacterWithDeploy (+0x250). Times in ms.
   */
  spawn: { unit: UnitDef; number: number; pauseTime: number; interval: number; startTime: number; radius: number; angleShift: number;
    withDeploy: boolean } | null;
  /** SpawnRadius / SpawnAngleShift (also read by the summon formation). */
  spawnRadius: number;
  spawnAngleShift: number;
  jumpEnabled: boolean;
  /** Hovering (crosses water in path costs, 0xf827c0). */
  hovering: boolean;
  /** Fly straight at the target instead of following the path (FlyDirectPaths). */
  flyDirectPaths: boolean;
  /** Stop-and-go walking: StopMovementAfterMS / WaitMS (movement +0x1f0). */
  stopMoveMs: number;
  waitMs: number;
  jumpSpeed: number;
  /** Charge: distance (A: data value x10 game units) of uninterrupted travel, speed %, first-hit damage. */
  chargeRange: number;
  chargeSpeedMul: number;
  damageSpecial: number;
  manaOnDeathForOpponent: number;
  /** Elixir Collector: elixir per period (x10000) and period ms; elixir to owner on death (x10000). */
  manaCollect: number;
  manaPeriod: number;
  manaOnDeath: number;
  /** VariableDamage columns as scaled stages (bot and viewer; the engine runs `seq`) and their times (loader default 0). */
  variableDamage: [number, number, number] | null;
  variableDamageTime: [number, number];
  /**
   * Attack sequence (CharacterData +0x2a8 entries, +0x3c8 index list, +0x3d4 count, +0x3d8 mode): mode 3
   * (the entry picked by the attack timer, 0xde0e5c) for VariableDamage characters (Inferno Tower and
   * Dragon), else mode 0 with at most one entry, which the attack paths ignore.
   */
  seqMode: number;
  seq: AttackSeqEntry[];
  /** Extra targets per attack and buff on damage (Electro Wizard). */
  multipleTargets: number;
  /** AllTargetsHit (+0x3a2): an extra target slot without a target hits the current target again (0xfc7e20). */
  allTargetsHit: boolean;
  buffOnDamage: BuffDef | null;
  buffOnDamageTime: number;
  /**
   * OverrideAttackFinishTime (+0x6b8) / AttackFinishTime (+0x6b4): the finish wait of 0xfc7e20 is
   * AttackFinishTime instead of ATTACK_FINISH_TIME_MS, and the target-removal notice 0xfc7640 does not
   * start it (Valkyrie, Bowler, Electro Wizard, Electro Dragon).
   */
  overrideFinish: boolean;
  attackFinishTime: number;
  /** Tesla: hides underground when idle. */
  hides: boolean;
  hideTime: number;
  upTime: number;
  /** Miner / Goblin Drill: tunnels from the King tower at this speed (units per tick). */
  spawnPathfindSpeed: number;
  /** MorphKeepTarget (+0x58d) and KeepTargetWithPendingDamage (+0x840): pending-damage overkill exemptions. */
  morphKeepTarget: boolean;
  keepTargetWithPendingDamage: boolean;
  /** Deploy-zone map (0xfa2c0c): NoDeploySizeW/H tiles (+0x80c/+0x810), TileSizeOverride (+0x4e8), IsSummoner (+0x572). */
  noDeployW: number;
  noDeployH: number;
  tileSizeOverride: number;
  isSummoner: boolean;
  /** SpawnPathfindMorph (+0x268): what a tunnelling character becomes (Goblin Drill); the deploy check uses its footprint. */
  spawnPathfindMorph: UnitDef | null;
  /** Mechanics this definition uses that the engine does not implement yet. */
  unsupported: string[];
}

export interface SummonEntry {
  unit: UnitDef;
  count: number;
  radius: number;
  deployDelay: number; // ms between members
  /** Explicit offsets (SummonCharactersOffsets) and per-member delays (SummonCharactersDelayList). */
  offsets: [number, number][] | null;
  delays: number[] | null;
  /** CustomDeployTime override (ms), -1 = the unit's own DeployTime. */
  deployTime: number;
  /** Placement role for two-group cards: 'main' | 'second'. */
  role: 'main' | 'second';
}

/** Native summon parameters (SpellData fields read by Deploy_summon 0xd423bc). */
export interface SummonSpec {
  /** SummonCharacter and SummonNumber (loaded as max(v,1)); the count is 0 without a SummonCharacter. */
  main: UnitDef | null;
  number: number;
  second: UnitDef | null;
  secondCount: number;
  /** SummonCharactersList with SummonCharactersOffsetsX/Y and SummonCharactersDelayList. */
  list: UnitDef[];
  offsetsX: number[];
  offsetsY: number[];
  delayList: number[];
  radius: number;
  width: number;
  deployDelay: number;
  deployDelaySecond: number;
}

export interface CardDef {
  name: string;
  type: 'troop' | 'building' | 'spell';
  rarity: string;
  cost: number; // elixir
  level: number;
  summon: SummonEntry[];
  spec: SummonSpec;
  /** Spell projectile (Fireball, Arrows...) */
  spellProjectile: ProjectileDef | null;
  /** Offsets for characters spawned by the spell projectile (Goblin Barrel). */
  spawnOffsets: [number, number][] | null;
  spellRadius: number;
  /** Area effect spell (Zap, Freeze, Poison, Lightning...). */
  areaEffect: AreaEffectDef | null;
  waves: number;
  waveInterval: number; // ms
  /** MultipleProjectiles per wave, ProjectileInterval between them (ms), CustomFirstProjectile. */
  multipleProjectiles: number;
  projectileInterval: number;
  firstProjectile: ProjectileDef | null;
  spellAsDeploy: boolean;
  canDeployOnEnemySide: boolean;
  canPlaceOnWater: boolean;
  /** CanPlaceOnBuildings (+0x17d) and DeployWTileMargin (+0x1a4), read by the deploy check 0xf9e16c. */
  canPlaceOnBuildings: boolean;
  deployWTileMargin: number;
  unsupported: string[];
}

// Fields whose mechanics the engine implements (others make a unit "unsupported").
/**
 * Character columns a supported card may carry. Most are read into UnitDef; these are accepted but only
 * the view uses them (no reader in the ported logic): AttackShakeTime and TryToFinishAttackAnimation /
 * ...BeforeNextAttack (read by the animation code 0x840d44), VisualHitSpeed (stored in the attack-sequence
 * entry +0xa4 with its effects), AttackDashTime (0xfc7e20 calls the view's attack start vt+0x40 that much
 * before the hit, with an Actor_faceActor that repeats the facing faceTarget set the same update).
 * AllTargetsHit, AttackFinishTime and OverrideAttackFinishTime are read (UnitDef).
 */
const UNIT_FIELDS_OK = new Set([
  'Name', 'Rarity', '_kind', 'Hitpoints', 'Damage', 'HitSpeed', 'LoadTime', 'Range', 'MinimumRange', 'SightRange',
  'Speed', 'CollisionRadius', 'Mass', 'DeployTime', 'DeployDelay', 'AttacksGround', 'AttacksAir',
  'TargetOnlyBuildings', 'TargetOnlyTroops', 'FlyingHeight', 'Projectile', 'ProjectileStartRadius',
  'ProjectileStartZ', 'ProjectileYOffset', 'AreaDamageRadius', 'SelfAsAoeCenter', 'IgnorePushback', 'Kamikaze',
  'CrownTowerDamagePercent', 'SpawnRadius', 'WalkingSpeedTweakPercentage', 'SightClip', 'SightClipSide',
  'StopMovementAfterMS', 'WaitMS', 'AttackFinishTime', 'OverrideAttackFinishTime', 'TryToFinishAttackAnimation',
  'TryToFinishAttackAnimationBeforeNextAttack', 'AttackDashTime', 'SpawnAngleShift', 'IsBuilding', 'Ability',
  'ClonedVersion', 'IgnoreBuff', 'IgnoreResurrect', 'IgnoreClone', 'CustomSpawnFilter', 'LifeTime',
  'DeathAreaEffect', 'DeathSpawnCharacter', 'DeathSpawnCount', 'DeathSpawnRadius', 'DeathSpawnDeployTime',
  'DeathSpawnPushback', 'SpawnCharacter', 'SpawnNumber', 'SpawnPauseTime', 'SpawnInterval', 'SpawnStartTime',
  'SpawnCharacterWithDeploy', 'ShieldHitpoints', 'JumpEnabled', 'Hovering', 'FlyDirectPaths', 'JumpHeight', 'JumpSpeed', 'ChargeRange', 'ChargeSpeedMultiplier', 'DamageSpecial',
  'AttackShakeTime', 'ManaOnDeathForOpponent', 'ManaCollectAmount', 'ManaGenerateTimeMs', 'ManaOnDeath',
  'VariableDamage2', 'VariableDamage3', 'VariableDamageTime1', 'VariableDamageTime2', 'VisualHitSpeed', 'MultipleTargets',
  'AllTargetsHit', 'BuffOnDamage', 'BuffOnDamageTime', 'HidesWhenNotAttacking', 'HideTimeMs', 'UpTimeMs', 'SpawnPathfindSpeed',
  'MorphKeepTarget', 'KeepTargetWithPendingDamage', 'NoDeploySizeW', 'NoDeploySizeH', 'TileSizeOverride',
]);
/** Starting actions the engine implements (JumpHack: pushback immunity while jumping, see UnitDef.jumpHack). */
const IGNORABLE_ACTIONS = new Set(['JumpHack_Check_Jump_Interval']);
/**
 * Projectile columns a supported card may carry. View-only (the loader 0xe5b14c stores them, no reader
 * in the ported logic): ShakesTargets, AlwaysResetAnimation. ProjectileStartExtraRadius and
 * DamageScalingMode are read (ProjectileDef.startExtraRadius, the tower scaling kind).
 */
const PROJECTILE_FIELDS_OK = new Set([
  'Name', 'Rarity', 'Speed', 'Damage', 'Radius', 'Homing', 'AoeToAir', 'AoeToGround', 'OnlyEnemies', 'Gravity',
  'CrownTowerDamagePercent', 'DeflectBehaviour', 'DeflectRadius', 'Pushback', 'PushbackAll', 'DamageScalingMode',
  'ConstantHeight', 'HideWhenDelayed', 'MinDistance', 'ShakesTargets', 'TargetBuff', 'BuffTime',
  'ChainedHitCount', 'ChainedHitRadius', 'ProjectileRange', 'ProjectileRadius', 'ProjectileRadiusY', 'PingpongVisualTime',
  'SpawnCharacter', 'SpawnCharacterCount', 'SpawnCharacterDeployTime', 'SpawnConstPriority', 'AlwaysResetAnimation',
  'SpawnProjectile', 'SpawnChain', 'RadiusY', 'ProjectileStartExtraRadius', 'HomingMinDistance', 'HomingTime',
  'MaximumTargets', 'DoDirectionalPushback',
]);
const AEO_FIELDS_OK = new Set([
  'Name', 'Rarity', 'Radius', 'LifeDuration', 'HitSpeed', 'HitSpeedOffset', 'Damage', 'Buff', 'BuffTime',
  'CapBuffTimeToAreaEffectTime', 'Filter', 'HitBiggestTargets', 'MaximumTargets', 'OneHitPerTarget', 'Projectile',
  'ProjectileStartHeight', 'SpawnAreaEffectObject', 'Pushback', 'OnlyEnemies', 'CrownTowerDamagePercent',
  'PushbackAll', 'RelativePushback', 'TargetProjectiles',
]);
const BUFF_FIELDS_OK = new Set([
  'Name', 'Rarity', 'SpeedMultiplier', 'HitSpeedMultiplier', 'SpawnSpeedMultiplier', 'DamagePerSecond', 'HitFrequency',
  'HealPerSecond', 'CrownTowerDamagePercent', 'BuildingDamagePercent', 'DamageReduction', 'EnableStacking',
  'HitTickFromSource', 'NoEffectToCrownTowers', 'TID', 'CrownTowerDamagePerHit',
]);

export class Defs {
  readonly data: GameData;
  private unitCache = new Map<string, UnitDef>();
  private projCache = new Map<string, ProjectileDef>();
  private cardCache = new Map<string, CardDef>();
  private aeoCache = new Map<string, AreaEffectDef>();
  private buffCache = new Map<string, BuffDef>();
  private filterCache = new Map<string, FilterDef>();

  constructor(data: GameData) {
    this.data = data;
  }

  filter(name: string): FilterDef {
    const hit = this.filterCache.get(name);
    if (hit) return hit;
    const r = this.data.filters[name] ?? {};
    const fl = r.Filters;
    const list: string[] = Array.isArray(fl) ? (fl as string[]) : typeof fl === 'string' ? [fl] : [];
    const def: FilterDef = {
      name,
      enemy: r.MatchTeamEnemy === true,
      own: r.MatchTeamOwn === true,
      buildingsOnly: r.MatchTypeBuildings === true && r.MatchTypeCharacters !== true,
      towersOnly: r.MatchTowers === true,
      exBuildings: list.includes('Buildings'),
      exKing: list.includes('Summoner') || list.includes('Towers'),
      exPrincess: list.includes('PrincessTowers') || list.includes('Towers'),
      exTowers: list.includes('Towers'),
      exFlying: list.includes('Flying'),
      include: Array.isArray(r.IncludeCharactersWithData) ? (r.IncludeCharactersWithData as string[]) : null,
      exclude: Array.isArray(r.ExcludeCharactersWithData) ? (r.ExcludeCharactersWithData as string[]) : null,
    };
    this.filterCache.set(name, def);
    return def;
  }

  buff(name: string, level: number): BuffDef {
    const key = `${name}@${level}`;
    const hit = this.buffCache.get(key);
    if (hit) return hit;
    const r = this.data.buffs[name];
    if (!r) throw new Error(`unknown buff ${name}`);
    const rarity = str(r, 'Rarity');
    const unsupported: string[] = [];
    for (const k of Object.keys(r)) if (!BUFF_FIELDS_OK.has(k)) unsupported.push(`${name}.${k}`);
    const def: BuffDef = {
      name,
      speedMul: num(r, 'SpeedMultiplier'),
      hitSpeedMul: num(r, 'HitSpeedMultiplier'),
      spawnSpeedMul: num(r, 'SpawnSpeedMultiplier'),
      dps: scaleStat(this.data, num(r, 'DamagePerSecond'), level, rarity),
      hitFrequency: num(r, 'HitFrequency'), // loader 0xddb0d8 default 0: no damage/heal ticks
      crownTowerDamagePerHit: scaleStat(this.data, num(r, 'CrownTowerDamagePerHit'), level, rarity),
      hps: scaleStat(this.data, num(r, 'HealPerSecond'), level, rarity),
      crownTowerDamagePercent: num(r, 'CrownTowerDamagePercent'),
      buildingDamagePercent: num(r, 'BuildingDamagePercent'),
      damageReduction: num(r, 'DamageReduction'),
      stacking: bool(r, 'EnableStacking'),
      unsupported,
    };
    this.buffCache.set(key, def);
    return def;
  }

  areaEffect(name: string, level: number): AreaEffectDef {
    const key = `${name}@${level}`;
    const hit = this.aeoCache.get(key);
    if (hit) return hit;
    const r = this.data.areaEffects[name];
    if (!r) throw new Error(`unknown area effect ${name}`);
    const rarity = str(r, 'Rarity');
    const unsupported: string[] = [];
    for (const k of Object.keys(r)) if (!AEO_FIELDS_OK.has(k)) unsupported.push(`${name}.${k}`);
    const dmg = damageOf(r, this.data.damageTypes);
    const buffName = str(r, 'Buff');
    const buff = buffName ? this.buff(buffName, level) : null;
    if (buff) unsupported.push(...buff.unsupported);
    const projName = str(r, 'Projectile');
    const spawnName = str(r, 'SpawnAreaEffectObject');
    const filterName = str(r, 'Filter');
    const def: AreaEffectDef = {
      name,
      radius: num(r, 'Radius'),
      lifeDuration: num(r, 'LifeDuration'),
      hitSpeed: num(r, 'HitSpeed'),
      hitSpeedOffset: num(r, 'HitSpeedOffset'),
      damage: scaleStat(this.data, dmg.base, level, rarity),
      towerDamage: dmg.tower >= 0 ? scaleStat(this.data, dmg.tower, level, rarity) : -1,
      crownTowerDamagePercent: num(r, 'CrownTowerDamagePercent'),
      buff,
      buffTime: num(r, 'BuffTime'),
      capBuffTime: bool(r, 'CapBuffTimeToAreaEffectTime'),
      filter: filterName ? this.filter(filterName) : null,
      onlyEnemies: r.OnlyEnemies !== false,
      hitBiggest: bool(r, 'HitBiggestTargets'),
      maxTargets: num(r, 'MaximumTargets'),
      oneHitPerTarget: bool(r, 'OneHitPerTarget'),
      projectile: projName ? this.projectile(projName, level) : null,
      spawnAreaEffect: spawnName ? this.areaEffect(spawnName, level) : null,
      pushback: num(r, 'Pushback'),
      pushbackAll: bool(r, 'PushbackAll'),
      relativePushback: bool(r, 'RelativePushback'),
      // AreaEffectData loader 0xdc2e74 reads TargetProjectiles with default 1 (+0xbd)
      targetProjectiles: r.TargetProjectiles !== false,
      projectileStartHeight: num(r, 'ProjectileStartHeight'),
      unsupported,
    };
    if (def.spawnAreaEffect) unsupported.push(...def.spawnAreaEffect.unsupported);
    this.aeoCache.set(key, def);
    return def;
  }

  projectile(name: string, level: number): ProjectileDef {
    const key = `${name}@${level}`;
    const hit = this.projCache.get(key);
    if (hit) return hit;
    const r = this.data.projectiles[name];
    if (!r) throw new Error(`unknown projectile ${name}`);
    const dmg = damageOf(r, this.data.damageTypes);
    const rarity = str(r, 'Rarity');
    const tb = str(r, 'TargetBuff');
    // DamageScalingMode (loader 0xe5b14c: KingTower 1, PrincessTower 2) picks the scaling kind 2·mode + 1 (0xe5ae54)
    const mode = r.DamageScalingMode === 'KingTower' ? 1 : r.DamageScalingMode === 'PrincessTower' ? 2 : 0;
    const scale = (v: number): number => (mode === 0 ? scaleStat(this.data, v, level, rarity) : scaleTower(this.data, v, level, mode === 1 ? 3 : 5));
    const def: ProjectileDef = {
      name,
      speed: num(r, 'Speed'),
      damage: scale(dmg.base),
      towerDamage: dmg.tower >= 0 ? scale(dmg.tower) : -1,
      crownTowerDamagePercent: num(r, 'CrownTowerDamagePercent'),
      radius: num(r, 'Radius'),
      homing: bool(r, 'Homing'),
      aoeToAir: bool(r, 'AoeToAir'),
      aoeToGround: bool(r, 'AoeToGround'),
      onlyEnemies: r.OnlyEnemies !== false,
      pushback: num(r, 'Pushback'),
      pushbackAll: bool(r, 'PushbackAll'),
      targetBuff: tb ? this.buff(tb, level) : null,
      buffTime: num(r, 'BuffTime'),
      chainCount: num(r, 'ChainedHitCount'),
      chainRadius: num(r, 'ChainedHitRadius'),
      range: num(r, 'ProjectileRange'),
      // 0xe5a908 [STAT]; Scatter "Circle" is the loader's mode 1 (0xe5a538)
      pierce: num(r, 'ProjectileRadius') > 0
        && ((!bool(r, 'Homing') && num(r, 'ProjectileRange') >= 1) || bool(r, 'CheckCollisions') || r.Scatter === 'Circle'),
      pierceRadius: num(r, 'ProjectileRadius'),
      pierceRadiusY: num(r, 'ProjectileRadiusY'),
      minDistance: num(r, 'MinDistance'),
      pingpongTime: num(r, 'PingpongVisualTime'),
      gravity: num(r, 'Gravity'),
      constantHeight: num(r, 'ConstantHeight'),
      homingTime: num(r, 'HomingTime'),
      homingMinDistance: num(r, 'HomingMinDistance'),
      checkCollisions: bool(r, 'CheckCollisions'),
      applyBuffBeforeDamage: bool(r, 'ApplyBuffBeforeDamage'),
      spawnUnit: null,
      spawnCount: 0,
      spawnDeployTime: num(r, 'SpawnCharacterDeployTime', -1),
      spawnConstPriority: bool(r, 'SpawnConstPriority'),
      spawnProjectile: null,
      spawnChain: 0,
      spawnProjCount: num(r, 'SpawnCount'),
      spawnProjRadius: num(r, 'SpawnRadius'),
      spawnAxisX: bool(r, 'SpawnAxisX'),
      spawnAxisY: bool(r, 'SpawnAxisY'),
      chainIsNew: bool(r, 'ChainIsNewProjectile'),
      maxTargets: num(r, 'MaximumTargets'),
      directionalPushback: bool(r, 'DoDirectionalPushback'),
      startExtraRadius: num(r, 'ProjectileStartExtraRadius'),
    };
    this.projCache.set(key, def);
    // loader 0xe5a538 [STAT]: SpawnCharacterCount is max(v, 1) with a SpawnCharacter, else 0;
    // SpawnChain is max(v, 1) with a SpawnProjectile
    const sc = str(r, 'SpawnCharacter');
    if (sc) {
      def.spawnUnit = this.unit(sc, level);
      def.spawnCount = Math.max(num(r, 'SpawnCharacterCount'), 1);
    }
    const sp = str(r, 'SpawnProjectile');
    if (sp) {
      def.spawnProjectile = this.projectile(sp, level);
      def.spawnChain = Math.max(num(r, 'SpawnChain'), 1);
    }
    return def;
  }

  unit(name: string, level: number): UnitDef {
    const key = `${name}@${level}`;
    const hit = this.unitCache.get(key);
    if (hit) return hit;
    const r = this.data.units[name];
    if (!r) throw new Error(`unknown unit ${name}`);
    const rarity = str(r, 'Rarity');
    const projName = str(r, 'Projectile');
    const unsupported: string[] = [];
    for (const k of Object.keys(r)) {
      if (UNIT_FIELDS_OK.has(k)) continue;
      if (k === 'OnStartingAction' && IGNORABLE_ACTIONS.has(r[k] as string)) continue;
      unsupported.push(k);
    }
    let projectile: ProjectileDef | null = null;
    if (projName) {
      projectile = this.projectile(projName, level);
      for (const k of Object.keys(this.data.projectiles[projName])) {
        if (!PROJECTILE_FIELDS_OK.has(k)) unsupported.push(`${projName}.${k}`);
      }
      if (projectile.targetBuff) unsupported.push(...projectile.targetBuff.unsupported);
    }
    const dmg = damageOf(r, this.data.damageTypes);
    const dae = str(r, 'DeathAreaEffect');
    const deathAreaEffect = dae ? this.areaEffect(dae, level) : null;
    if (deathAreaEffect) unsupported.push(...deathAreaEffect.unsupported);
    const dsc = str(r, 'DeathSpawnCharacter');
    let deathSpawn: UnitDef['deathSpawn'] = null;
    if (dsc) {
      const unit = this.unit(dsc, level);
      unsupported.push(...unit.unsupported.map((x) => `${dsc}.${x}`));
      deathSpawn = { unit, count: num(r, 'DeathSpawnCount', 1), radius: num(r, 'DeathSpawnRadius'), deployTime: num(r, 'DeathSpawnDeployTime', -1) };
    }
    const spc = str(r, 'SpawnCharacter');
    let spawn: UnitDef['spawn'] = null;
    if (spc) {
      const unit = this.unit(spc, level);
      unsupported.push(...unit.unsupported.map((x) => `${spc}.${x}`));
      spawn = {
        unit,
        number: num(r, 'SpawnNumber'),
        pauseTime: num(r, 'SpawnPauseTime'),
        interval: num(r, 'SpawnInterval'),
        startTime: num(r, 'SpawnStartTime'),
        radius: num(r, 'SpawnRadius'),
        angleShift: num(r, 'SpawnAngleShift'),
        withDeploy: bool(r, 'SpawnCharacterWithDeploy'),
      };
    }
    const def: UnitDef = {
      spawnRadius: num(r, 'SpawnRadius'),
      spawnAngleShift: num(r, 'SpawnAngleShift'),
      name,
      kind: r._kind === 'building' ? 'building' : 'character',
      rarity,
      level,
      hitpoints: scaleStat(this.data, num(r, 'Hitpoints'), level, rarity),
      shieldHitpoints: scaleStat(this.data, num(r, 'ShieldHitpoints'), level, rarity),
      damage: scaleStat(this.data, dmg.base, level, rarity),
      crownTowerDamagePercent: num(r, 'CrownTowerDamagePercent'),
      hitSpeed: num(r, 'HitSpeed'),
      loadTime: num(r, 'LoadTime'),
      range: num(r, 'Range'),
      minimumRange: num(r, 'MinimumRange'),
      sightRange: num(r, 'SightRange'),
      sightClip: num(r, 'SightClip'),
      sightClipSide: num(r, 'SightClipSide'),
      speed: walkSpeed(r),
      collisionRadius: num(r, 'CollisionRadius'),
      mass: normaliseMass(num(r, 'Mass'), num(r, 'CollisionRadius')),
      deployTime: num(r, 'DeployTime'),
      deployDelay: num(r, 'DeployDelay'),
      attacksGround: bool(r, 'AttacksGround'),
      attacksAir: bool(r, 'AttacksAir'),
      targetOnlyBuildings: bool(r, 'TargetOnlyBuildings'),
      targetOnlyTroops: bool(r, 'TargetOnlyTroops'),
      flyingHeight: num(r, 'FlyingHeight'),
      projectile,
      projectileStartRadius: num(r, 'ProjectileStartRadius'),
      projectileYOffset: num(r, 'ProjectileYOffset'),
      projectileStartZ: num(r, 'ProjectileStartZ'),
      jumpHack: r.OnStartingAction === 'JumpHack_Check_Jump_Interval',
      jumpHeight: num(r, 'JumpHeight'),
      areaDamageRadius: num(r, 'AreaDamageRadius'),
      selfAsAoeCenter: bool(r, 'SelfAsAoeCenter'),
      ignorePushback: bool(r, 'IgnorePushback'),
      kamikaze: bool(r, 'Kamikaze'),
      lifeTime: num(r, 'LifeTime'),
      deathAreaEffect,
      deathSpawn,
      spawn,
      jumpEnabled: bool(r, 'JumpEnabled'),
      hovering: bool(r, 'Hovering'),
      flyDirectPaths: bool(r, 'FlyDirectPaths'),
      stopMoveMs: num(r, 'StopMovementAfterMS'),
      waitMs: num(r, 'WaitMS'),
      jumpSpeed: num(r, 'JumpSpeed'),
      chargeRange: num(r, 'ChargeRange') * 10,
      chargeSpeedMul: num(r, 'ChargeSpeedMultiplier'),
      damageSpecial: scaleStat(this.data, num(r, 'DamageSpecial'), level, rarity),
      manaOnDeathForOpponent: num(r, 'ManaOnDeathForOpponent'),
      manaCollect: num(r, 'ManaCollectAmount') * ELIXIR_ONE,
      manaPeriod: num(r, 'ManaGenerateTimeMs'),
      manaOnDeath: num(r, 'ManaOnDeath') * ELIXIR_ONE,
      variableDamage: r.VariableDamage2 !== undefined
        ? [scaleStat(this.data, dmg.base, level, rarity), scaleStat(this.data, num(r, 'VariableDamage2'), level, rarity), scaleStat(this.data, num(r, 'VariableDamage3'), level, rarity)]
        : null,
      variableDamageTime: [num(r, 'VariableDamageTime1'), num(r, 'VariableDamageTime2')],
      ...attackSequence(r, (v) => scaleStat(this.data, v, level, rarity), dmg.base),
      multipleTargets: num(r, 'MultipleTargets', 1),
      allTargetsHit: bool(r, 'AllTargetsHit'),
      overrideFinish: bool(r, 'OverrideAttackFinishTime'),
      attackFinishTime: num(r, 'AttackFinishTime'),
      buffOnDamage: str(r, 'BuffOnDamage') ? this.buff(str(r, 'BuffOnDamage')!, level) : null,
      buffOnDamageTime: num(r, 'BuffOnDamageTime'),
      hides: bool(r, 'HidesWhenNotAttacking'),
      hideTime: num(r, 'HideTimeMs'),
      upTime: num(r, 'UpTimeMs'),
      spawnPathfindSpeed: num(r, 'SpawnPathfindSpeed'),
      morphKeepTarget: bool(r, 'MorphKeepTarget'),
      keepTargetWithPendingDamage: bool(r, 'KeepTargetWithPendingDamage'),
      noDeployW: num(r, 'NoDeploySizeW'),
      noDeployH: num(r, 'NoDeploySizeH'),
      tileSizeOverride: num(r, 'TileSizeOverride'),
      isSummoner: bool(r, 'IsSummoner'),
      spawnPathfindMorph: null,
      unsupported,
    };
    this.unitCache.set(key, def);
    const morph = str(r, 'SpawnPathfindMorph');
    if (morph && this.data.units[morph]) def.spawnPathfindMorph = this.unit(morph, level);
    return def;
  }

  /**
   * Crown-tower definition at tower level `level`: the King's hitpoints scale with kind 4 (IsSummoner) and a
   * Princess tower's with kind 6 (IsSummonerTower) (0xde0110), the projectiles' damage by their
   * DamageScalingMode (KingProjectile: KingTower, kind 3; TowerPrincessProjectile: PrincessTower, kind 5),
   * all through scaleTower (0xe7e418) [STAT].
   */
  tower(kind: 'king' | 'princess', level: number): UnitDef {
    const key = `#tower-${kind}@${level}`;
    const hit = this.unitCache.get(key);
    if (hit) return hit;
    const name = kind === 'king' ? 'KingTower' : 'PrincessTower';
    const r = this.data.units[name];
    const projName = str(r, 'Projectile')!;
    const p = this.data.projectiles[projName];
    const pmode = p.DamageScalingMode === 'KingTower' ? 3 : p.DamageScalingMode === 'PrincessTower' ? 5 : 0;
    if (pmode === 0) throw new Error(`${projName} has no tower DamageScalingMode`);
    const projectile: ProjectileDef = {
      name: projName,
      speed: num(p, 'Speed'),
      damage: scaleTower(this.data, damageOf(p, this.data.damageTypes).base, level, pmode),
      towerDamage: -1,
      crownTowerDamagePercent: 0,
      radius: 0,
      homing: bool(p, 'Homing'),
      aoeToAir: false,
      aoeToGround: false,
      onlyEnemies: true,
      pushback: 0,
      pushbackAll: false,
      targetBuff: null,
      buffTime: 0,
      chainCount: 0,
      chainRadius: 0,
      range: 0,
      pierce: false,
      pierceRadius: 0,
      pierceRadiusY: 0,
      minDistance: 0,
      pingpongTime: 0,
      gravity: num(p, 'Gravity'),
      constantHeight: num(p, 'ConstantHeight'),
      homingTime: 0,
      homingMinDistance: 0,
      checkCollisions: false,
      applyBuffBeforeDamage: false,
      spawnUnit: null,
      spawnCount: 0,
      spawnDeployTime: -1,
      spawnConstPriority: false,
      spawnProjectile: null,
      spawnChain: 0,
      spawnProjCount: 0,
      spawnProjRadius: 0,
      spawnAxisX: false,
      spawnAxisY: false,
      chainIsNew: false,
      maxTargets: 0,
      directionalPushback: false,
      startExtraRadius: 0,
    };
    const def: UnitDef = {
      name,
      kind: 'building',
      rarity: null,
      level,
      hitpoints: scaleTower(this.data, num(r, 'Hitpoints'), level, kind === 'king' ? 4 : 6),
      shieldHitpoints: 0,
      damage: 0,
      crownTowerDamagePercent: 0,
      hitSpeed: num(r, 'HitSpeed'),
      loadTime: num(r, 'LoadTime'),
      range: num(r, 'Range'),
      minimumRange: num(r, 'MinimumRange'),
      sightRange: num(r, 'SightRange'),
      sightClip: 0,
      sightClipSide: 0,
      speed: 0,
      collisionRadius: num(r, 'CollisionRadius'),
      // derived from CollisionRadius like any character data (0xdde618): 1400 / 1000 -> 20
      mass: normaliseMass(num(r, 'Mass'), num(r, 'CollisionRadius')),
      deployTime: 0,
      deployDelay: 0,
      attacksGround: bool(r, 'AttacksGround'),
      attacksAir: bool(r, 'AttacksAir'),
      targetOnlyBuildings: false,
      targetOnlyTroops: false,
      flyingHeight: 0,
      projectile,
      projectileStartRadius: num(r, 'ProjectileStartRadius'),
      projectileYOffset: num(r, 'ProjectileYOffset'),
      projectileStartZ: num(r, 'ProjectileStartZ'),
      jumpHack: false,
      jumpHeight: 0,
      areaDamageRadius: 0,
      selfAsAoeCenter: false,
      ignorePushback: true,
      kamikaze: false,
      lifeTime: 0,
      deathAreaEffect: null,
      deathSpawn: null,
      spawn: null,
      spawnRadius: 0,
      spawnAngleShift: 0,
      jumpEnabled: false,
      hovering: false,
      flyDirectPaths: false,
      stopMoveMs: 0,
      waitMs: 0,
      jumpSpeed: 0,
      chargeRange: 0,
      chargeSpeedMul: 0,
      damageSpecial: 0,
      manaOnDeathForOpponent: 0,
      manaCollect: 0,
      manaPeriod: 0,
      manaOnDeath: 0,
      variableDamage: null,
      variableDamageTime: [0, 0],
      seqMode: 0,
      seq: [],
      multipleTargets: 1,
      allTargetsHit: false,
      buffOnDamage: null,
      buffOnDamageTime: 0,
      overrideFinish: false,
      attackFinishTime: 0,
      hides: false,
      hideTime: 0,
      upTime: 0,
      spawnPathfindSpeed: 0,
      morphKeepTarget: false,
      keepTargetWithPendingDamage: false,
      noDeployW: num(r, 'NoDeploySizeW'),
      noDeployH: num(r, 'NoDeploySizeH'),
      tileSizeOverride: num(r, 'TileSizeOverride'),
      isSummoner: bool(r, 'IsSummoner'),
      spawnPathfindMorph: null,
      unsupported: [],
    };
    this.unitCache.set(key, def);
    return def;
  }

  card(name: string, level: number): CardDef {
    const key = `${name}@${level}`;
    const hit = this.cardCache.get(key);
    if (hit) return hit;
    const r = this.data.cards[name];
    if (!r) throw new Error(`unknown card ${name}`);
    const unsupported: string[] = [];
    const summon: CardDef['summon'] = [];
    const sc = str(r, 'SummonCharacter');
    const pushUnit = (n: string): UnitDef => {
      const unit = this.unit(n, level);
      unsupported.push(...unit.unsupported.map((u) => `${n}.${u}`));
      return unit;
    };
    const offs = (): [number, number][] | null => {
      const ox = r.SummonCharactersOffsetsX as number[] | undefined;
      const oy = r.SummonCharactersOffsetsY as number[] | undefined;
      return ox && oy ? ox.map((x, i) => [x, oy[i] ?? 0]) : null;
    };
    const customDeploy = num(r, 'CustomDeployTime', -1);
    if (sc) {
      summon.push({
        unit: pushUnit(sc), count: num(r, 'SummonNumber', 1), radius: num(r, 'SummonRadius'),
        deployDelay: num(r, 'SummonDeployDelay'), offsets: null, delays: null, deployTime: customDeploy, role: 'main',
      });
    }
    const sc2 = str(r, 'SummonCharacterSecond');
    if (sc2) {
      summon.push({
        unit: pushUnit(sc2), count: num(r, 'SummonCharacterSecondCount', 1), radius: num(r, 'SummonRadius'),
        deployDelay: num(r, 'SummonDeployDelaySecond', num(r, 'SummonDeployDelay')), offsets: null, delays: null,
        deployTime: customDeploy, role: 'second',
      });
    }
    const list = r.SummonCharactersList as string[] | undefined;
    if (list) {
      const o = offs();
      const dl = r.SummonCharactersDelayList as number[] | undefined;
      const step = num(r, 'SummonDeployDelay');
      list.forEach((n, i) => {
        summon.push({
          unit: pushUnit(n), count: 1, radius: 0, deployDelay: 0, offsets: o ? [o[i] ?? [0, 0]] : null,
          delays: [dl ? (dl[i] ?? 0) : i * step], deployTime: customDeploy, role: 'main',
        });
      });
    }
    for (const k of ['Mirror', 'FullLaneDeploy']) {
      if (r[k] !== undefined) unsupported.push(k);
    }
    const projName = str(r, 'Projectile');
    const spellProjectile = projName ? this.projectile(projName, level) : null;
    if (projName) {
      for (const k of Object.keys(this.data.projectiles[projName])) {
        if (!PROJECTILE_FIELDS_OK.has(k)) unsupported.push(`${projName}.${k}`);
      }
    }
    const aeoName = str(r, 'AreaEffectObject');
    const areaEffect = aeoName ? this.areaEffect(aeoName, level) : null;
    if (areaEffect) unsupported.push(...areaEffect.unsupported);
    if (spellProjectile?.targetBuff) unsupported.push(...spellProjectile.targetBuff.unsupported);
    const type = (r._type as CardDef['type']) ?? 'troop';
    if (!sc && !projName && !aeoName && !list) unsupported.push('no SummonCharacter/Projectile/AreaEffectObject');
    const sc2u = sc2 ? summon.find((e) => e.role === 'second')!.unit : null;
    const spec: SummonSpec = {
      main: sc ? summon[0].unit : null,
      number: Math.max(num(r, 'SummonNumber', 1), 1),
      second: sc2u,
      secondCount: sc2 ? num(r, 'SummonCharacterSecondCount') : 0,
      list: list ? summon.filter((e) => e.offsets !== null || e.delays !== null).map((e) => e.unit) : [],
      offsetsX: (r.SummonCharactersOffsetsX as number[] | undefined) ?? [],
      offsetsY: (r.SummonCharactersOffsetsY as number[] | undefined) ?? [],
      delayList: (r.SummonCharactersDelayList as number[] | undefined) ?? [],
      radius: num(r, 'SummonRadius'),
      width: num(r, 'SummonWidth'),
      deployDelay: num(r, 'SummonDeployDelay'),
      deployDelaySecond: num(r, 'SummonDeployDelaySecond'),
    };
    const def: CardDef = {
      name,
      type,
      rarity: str(r, 'Rarity') ?? 'Common',
      cost: num(r, 'ManaCost'),
      level,
      summon,
      spec,
      spellProjectile,
      spawnOffsets: spellProjectile?.spawnUnit ? offs() : null,
      spellRadius: num(r, 'Radius'),
      areaEffect,
      waves: num(r, 'ProjectileWaves', 1),
      waveInterval: num(r, 'ProjectileWaveInterval'),
      multipleProjectiles: num(r, 'MultipleProjectiles', 1),
      projectileInterval: num(r, 'ProjectileInterval'),
      firstProjectile: str(r, 'CustomFirstProjectile') ? this.projectile(str(r, 'CustomFirstProjectile')!, level) : null,
      spellAsDeploy: bool(r, 'SpellAsDeploy'),
      canDeployOnEnemySide: bool(r, 'CanDeployOnEnemySide'),
      canPlaceOnWater: bool(r, 'CanPlaceOnWater'),
      canPlaceOnBuildings: bool(r, 'CanPlaceOnBuildings'),
      deployWTileMargin: num(r, 'DeployWTileMargin'),
      unsupported,
    };
    this.cardCache.set(key, def);
    return def;
  }
}

/** Playable (not event-only) cards whose every mechanic the engine implements, by elixir then name. */
export function supportedCards(defs: Defs, level = 11): string[] {
  const out: { name: string; cost: number }[] = [];
  for (const [name, r] of Object.entries(defs.data.cards)) {
    if (r.NotVisible === true || r.NotInUse === true) continue;
    let card: CardDef;
    try { card = defs.card(name, level); } catch { continue; }
    if (card.unsupported.length === 0) out.push({ name, cost: card.cost });
  }
  out.sort((a, b) => a.cost - b.cost || (a.name < b.name ? -1 : 1));
  return out.map((c) => c.name);
}

/** Cards the engine fully supports in this milestone (checked by tests). */
export const M1_CARDS = [
  'Knight', 'Archer', 'Giant', 'Musketeer', 'MiniPekka', 'Valkyrie', 'BabyDragon', 'Minions', 'MinionHorde',
  'Skeletons', 'SkeletonArmy', 'Goblins', 'SpearGoblins', 'Bomber', 'Wizard', 'Barbarians', 'Pekka', 'MegaMinion',
  'RoyalGiant', 'FireSpirits', 'Bats', 'Fireball', 'Arrows',
] as const;
